import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router';
import isPropValid from '@emotion/is-prop-valid';
import { Box, styled, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import {
  useConstructUrlOnIntetWorkflowCreation,
  useEmailMode,
  useGetBuilderQueryParams,
  useGetHandoffConfigurationForWorkflow,
  useGetIntentTitle,
  useGetIsTemplateIntentWorkflow,
  useGoBackToLandingPage,
  useInitPusher,
  useWorkflowBuilderMode,
} from '../hooks';
import WorkflowConflictMessage from '../WorkflowConflictMessage';
import { VersionDropdown } from './VersionDropdown';
import { duplicateIntentWorkflow } from 'src/actions/workflow-builder/workflowBuilderActions';
import BackIcon from 'src/assets/images/actions-back-icon.svg';
import BackIconWhite from 'src/assets/images/actions-back-icon-white.svg';
import {
  useIsFeatureFlagEnabled,
  useIsWorkflowUsedByWidgetConfiguration,
} from 'src/hooks/hooks';
import { useDeleteIntentEventSubscription } from 'src/hooks/PusherEventHooks';
import { useActiveAbTestForWorkflow } from 'src/hooks/useActiveAbTestForWorkflow';
import { buildPostDuplicateSearch } from 'src/pages/workflow-builder/buildPostDuplicateSearch';
import { DeleteWorkflowDialog } from 'src/pages/workflow-builder/DeleteWorkflowDialog';
import { WorkflowDropdownMenu } from 'src/pages/workflow-builder/DropdownMenu';
import { useWidgetMetricBreakdownById } from 'src/pages/workflow-builder/intent-workflows-table/hooks/useWidgetMetricBreakdownById';
import { selectUserCan } from 'src/reducers/userReducer/userReducer';
import { selectCanvasWorkflowBuilder } from 'src/reducers/workflowBuilderReducer/workflowBuilderReducer';
import {
  dashboardApi,
  useGetPaidPlanDetailsQuery,
} from 'src/services/dashboard-api';
import {
  useGetWorkflowsForIntentQuery,
  useLazyGetWorkflowsForIntentQuery,
} from 'src/services/workflow-builder-canvas/workflowBuilderCanvasApi';
import { useGetHandoffConfigurationsQuery } from 'src/services/workflow-builder-metrics';
import { CanvasWorkflowBuilderState } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import { getIntent } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice.thunks';
import { getDiscoverAutomations } from 'src/slices/data/thunks';
import { getWidgetConfiguration } from 'src/slices/solve-config/solveConfigSlice';
import {
  openGlobalIntentDrawer,
  setGlobalHandoffDrawerOptions,
} from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { CommonIntentWorkflowType, Routes } from 'src/utils/enums';
import { getUserOrgCode } from 'src/utils/getUserOrgCode';
import {
  isIntentDuplicable,
  isIntentEditable,
} from 'src/utils/solve/intentUtils';

interface WorkflowBuilderEditHeader {
  isDarkMode?: boolean;
  isFromNavbar?: boolean;
}

const WorkflowBuilderEditHeader = ({
  isDarkMode = false,
  isFromNavbar = false,
}: WorkflowBuilderEditHeader) => {
  const { t } = useTranslation('workflow-builder-edit');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { palette } = useTheme();

  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const isFallbackWorkflowEnabled =
    useIsFeatureFlagEnabled('fallback_workflow');

  const [isIntentDeleted, setIsIntentDeleted] = useState(false);

  const { intentId, view } = useGetBuilderQueryParams();
  const { emailMode } = useEmailMode();

  const { breakdownById } = useWidgetMetricBreakdownById();

  const { data: workflowsForIntent, isLoading } = useGetWorkflowsForIntentQuery(
    { intentDefinitionId: intentId },
    { skip: !intentId },
  );
  const [getWorkflowsForIntentTrigger] = useLazyGetWorkflowsForIntentQuery();

  const [params] = useSearchParams();
  const startDate = params.get('start') ?? '';
  const endDate = params.get('end') ?? '';

  const intentTitle = useGetIntentTitle(intentId);
  const isTemplateIntentWorkflow = useGetIsTemplateIntentWorkflow();
  const workflowBuilderData: CanvasWorkflowBuilderState = useSelector(
    selectCanvasWorkflowBuilder,
  );
  const handoffConfiguration = useGetHandoffConfigurationForWorkflow();
  const isHandoff = handoffConfiguration !== undefined;
  const { refetch: refetchHandoffConfigurations } =
    useGetHandoffConfigurationsQuery();

  const userCanDeleteAction = useSelector(
    selectUserCan('delete_custom_intent'),
  );
  const { data: paidPlanData } = useGetPaidPlanDetailsQuery();
  const isWorkflowUsedByWidgetConfig = useIsWorkflowUsedByWidgetConfiguration();

  const goBackToLandingPage = useGoBackToLandingPage();
  const [builderMode] = useWorkflowBuilderMode();

  const workflowId = workflowBuilderData?.intent_workflow_id || '';
  const isDiscoverEnabled = paidPlanData?.enablement_info.discover;

  const { hasAbTest } = useActiveAbTestForWorkflow(workflowId);

  useConstructUrlOnIntetWorkflowCreation();

  useInitPusher();

  useDeleteIntentEventSubscription({
    onEvent: useCallback(() => {
      setIsIntentDeleted(true);
    }, []),
    orgCode: getUserOrgCode(),
  });

  useEffect(() => {
    dispatch(getWidgetConfiguration());
  }, [dispatch]);

  const duplicateWorkflow = async () => {
    const isWebCallView = view === 'web_call';
    const workflowIdToDuplicate = workflowsForIntent?.widget_workflow_id;

    if (!workflowIdToDuplicate || isLoading) {
      console.error('Data not loaded or workflow_id missing.');
      return;
    }
    try {
      const data = await dispatch(
        duplicateIntentWorkflow({
          workflowId: workflowIdToDuplicate,
        }),
      ).unwrap();
      await refetchHandoffConfigurations();
      const newWorkflowId = data.canvas.intent_workflow_id;
      await dispatch(getIntent(data.canvas.intent_id));
      const search = await buildPostDuplicateSearch({
        fetchTargetWorkflowId: isWebCallView
          ? async () => {
              const newWorkflows = await getWorkflowsForIntentTrigger({
                intentDefinitionId: data.canvas.intent_id,
              }).unwrap();
              return newWorkflows?.web_call_workflow_id;
            }
          : undefined,
        fetchVoiceWorkflowId: async () => {
          const newWorkflows = await getWorkflowsForIntentTrigger({
            intentDefinitionId: data.canvas.intent_id,
          }).unwrap();
          return newWorkflows?.voice_workflow_id;
        },
        intentId: data.canvas.intent_id,
        intentWorkflowId: newWorkflowId,
        subflowType: handoffConfiguration?.subflow_type,
        targetView: isWebCallView ? 'web_call' : undefined,
        targetWorkflowId: isWebCallView
          ? data.canvas.web_call_workflow_id
          : undefined,
      });
      navigate({
        pathname: Routes.WORKFLOW_BUILDER_EDIT,
        search,
      });
      if (!isHandoff) {
        dispatch(
          openGlobalIntentDrawer({
            type: isWebCallView ? 'web_call' : 'widget',
          }),
        );
      }
      // because we don't yet use rtk query for all intent apis,
      // manually invalidate the cache tag to trigger a refetch
      dispatch(dashboardApi.util.invalidateTags(['Intents']));
    } catch (e) {
      console.error(e);
    }
  };

  const onEditHandoffClicked = () => {
    dispatch(
      setGlobalHandoffDrawerOptions({
        handoffToEdit: {
          handoffName: intentTitle,
          intentId: intentId,
          intentWorkflowId: workflowId,
          isDefaultHandoff: handoffConfiguration?.is_default_handoff ?? false,
        },
        isCreateHandoffDrawerOpen: true,
        isEditing: true,
        subflowType: handoffConfiguration?.subflow_type || null,
      }),
    );
  };

  const onEditIntentClicked = async () => {
    await dispatch(
      getIntent(
        (workflowBuilderData.intent_id ||
          workflowBuilderData.intent?.intent_id) as string,
      ),
    );
    if (isDiscoverEnabled) {
      await dispatch(getDiscoverAutomations());
    }
    dispatch(
      openGlobalIntentDrawer({
        type: view,
      }),
    );
  };

  const shouldEnableViewInsight = React.useMemo(() => {
    const fallbackEntry = Object.values(breakdownById || {}).find(
      entry => entry.intent_id === workflowBuilderData.intent_id,
    );
    return Boolean(fallbackEntry?.inquiries) && view === 'widget';
  }, [breakdownById, view, workflowBuilderData.intent_id]);

  const onViewInsightClick = (intentId: string) => {
    const start = parseInt(startDate) * 1000;
    const end = parseInt(endDate) * 1000;
    const search = `?tableTab=1&articles_sort_column=times_surfaced&date=${start}_${end}&article_sort_direction=DESC`;
    const pathName = Routes.SOLVE_INSIGHTS_WORKFLOW_DETAIL.replace(
      ':workflowId',
      intentId,
    );

    navigate({
      pathname: pathName,
      search: search,
    });
  };

  return (
    <>
      <Header isDarkMode={isDarkMode} isFromNavbar={isFromNavbar}>
        <BackButton
          disabled={workflowBuilderData.loading}
          onClick={goBackToLandingPage}
        >
          <img alt='' src={isDarkMode ? BackIconWhite : BackIcon} />
        </BackButton>
        {intentTitle && (
          <>
            <Typography
              color={isDarkMode ? palette.colors.white : palette.colors.black}
              variant='font24'
            >
              {intentTitle}
            </Typography>
            {!isTemplateIntentWorkflow && (
              <Box
                sx={{
                  height: '30px',
                  marginLeft: '10px',
                  width: '30px',
                }}
              >
                <WorkflowDropdownMenu
                  deleteDisabledTooltip={
                    hasAbTest
                      ? 'Cannot delete an agent that is part of an A/B test'
                      : undefined
                  }
                  intentId={intentId}
                  intentName={intentTitle}
                  isCommonIntentWorkflowType={Object.values(
                    CommonIntentWorkflowType,
                  ).includes(intentId as CommonIntentWorkflowType)}
                  isDarkMode={isDarkMode}
                  isDefaultHandoff={
                    handoffConfiguration?.is_default_handoff ?? false
                  }
                  isDisabled={!!workflowBuilderData?.loading}
                  isHandoff={isHandoff}
                  // Belt-and-suspenders with useIsAbTestAvailable (which
                  // gates on /assist/* routes). Covers the legacy
                  // ?channel=playbook URL that lives outside /assist/*.
                  isPlaybookChannel={view === 'playbook'}
                  onDeleteClicked={() => setIsDeleteDialogVisible(true)}
                  onDuplicateClicked={duplicateWorkflow}
                  onEditClicked={() => {
                    isHandoff ? onEditHandoffClicked() : onEditIntentClicked();
                  }}
                  onViewInsightClick={() =>
                    onViewInsightClick(
                      workflowBuilderData.intent_workflow_id || '',
                    )
                  }
                  shouldEnableDelete={
                    userCanDeleteAction &&
                    intentId !== CommonIntentWorkflowType.KNOWLEDGE_ARTICLE &&
                    intentId !== CommonIntentWorkflowType.FALLBACK &&
                    (isFallbackWorkflowEnabled ||
                      !(handoffConfiguration?.is_default_handoff ?? false)) &&
                    !isWorkflowUsedByWidgetConfig(workflowId) &&
                    !hasAbTest
                  }
                  shouldEnableViewInsight={shouldEnableViewInsight}
                  shouldShowChats={view === 'widget'}
                  shouldShowDuplicate={isIntentDuplicable(intentId)}
                  shouldShowEdit={isIntentEditable(intentId)}
                  shouldShowPreview={false}
                  subflowType={handoffConfiguration?.subflow_type || null}
                  workflowId={workflowId}
                />
              </Box>
            )}
            {/* Version dropdown only for Autoflow workflows, hidden for Email Journey/Response tabs */}
            {builderMode === 'autoflow' &&
              !(
                view === 'email' &&
                (emailMode === 'Journey' || emailMode === 'Response')
              ) && (
                <Box
                  sx={{
                    marginLeft: '10px',
                  }}
                >
                  <VersionDropdown intentWorkflowId={workflowId} />
                </Box>
              )}
          </>
        )}
      </Header>
      <DeleteWorkflowDialog
        intentIdToDelete={intentId}
        isOpen={isDeleteDialogVisible}
        onClose={() => setIsDeleteDialogVisible(false)}
        onSuccess={goBackToLandingPage}
      />
      <WorkflowConflictMessage
        buttonMessage={t('header.intentDeletedConflict.button')}
        message={t('header.intentDeletedConflict.message')}
        onButtonClick={() => goBackToLandingPage()}
        open={isIntentDeleted}
        title={t('header.intentDeletedConflict.title')}
      />
    </>
  );
};

export default WorkflowBuilderEditHeader;

const Header = styled('div', {
  shouldForwardProp: prop =>
    isPropValid(String(prop)) &&
    prop !== 'isDarkMode' &&
    prop !== 'isFromNavbar',
})<{ isDarkMode: boolean; isFromNavbar: boolean }>`
  display: flex;
  justify-content: flex-start;
  padding: ${props => (props.isFromNavbar ? '0px' : '16px 24px 16px 40px')};
  background: ${props =>
    props.isDarkMode
      ? props.theme.palette.colors.primary[800]
      : props.theme.palette.background.default};
  border-bottom: ${props =>
    props.isFromNavbar
      ? '0px'
      : `1px solid ${props.theme.palette.colors.slate[200]}`};
  align-items: center;
  transition: inherit;
  flex: 1;
`;

const BackButton = styled('button')`
  margin-right: 20px;
  cursor: pointer;
  padding: 1px 2px 2px 2px;
  &:disabled {
    cursor: auto;
  }
  height: 26px;
  &:hover {
    background-color: transparent !important;
  }
`;

BackButton.defaultProps = {
  'aria-label': 'Back',
};
